import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { trim } from 'lodash'
import routesConfig, {
  gotoAdminEntryPathFromWizard,
  gotoAdminLandingPage,
  gotoEnduserEntryPathFromWizard
} from 'lib/routesConfig'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import {
  getInlineDeploymentStatus,
  resetInlineDeployment,
  resetSetupFlowError,
  saveDeploymentMethod,
  saveSetupFlowStep,
  initializeSetupFlow,
  setEmailServerSettings,
  setIsEmailInputError,
  setIsMissedServerDetection,
  setIsMissedServerVerify,
  setNewMxRecordsError,
  setOldMxRecordsError,
  setRegion,
  setSelectedRegionError,
  setSetupFlowStep,
  startInlineDeployment,
  msVerifyDomain
} from 'redux/features/emailServer/emailServerSlice'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { isEmailValid } from 'lib/validation'
import appFeatures from 'config/appFeatures'
import { UserRights, useUserRights } from 'components/pages/onboardWizard/onboardWizardLogicUserRights'
import appConfig from 'config/appConfig'
import {
  setIntercomUser,
  trackEventInAllServices,
  TRACKING_EVENTS,
  trackPageLeaveMixpanelEvent
} from 'lib/monitoring/monitoringService'
import { INLINE_DEPLOYMENT_STATUSES } from 'types/emailServer'
import {
  DeploymentSetupOptions,
  InitialSetupFlowSettings,
  IPIRAppTypes,
  SetupFlowEgdSteps,
  SetupFlowIpirSteps,
  SetupFlowCommonSteps,
  SetupFlowSteps
} from './types/egdWizardTypes'

export interface StepInfo {
  name: SetupFlowSteps
  skipped?: boolean
  active?: boolean
}

export interface State {
  currentStep: SetupFlowSteps
  stepInProgress: boolean
  loading: boolean
  nextDisabled: boolean
  steps: StepInfo[]
  inlineDeploymentInProgress: boolean
  completeSetupButton: boolean
}

export interface EventHandlers {
  onExit: () => void
  onNext: () => void
}

export type EmailFlowLogic = [State, EventHandlers]

export const useSetupFlowLogic = (props: InitialSetupFlowSettings): EmailFlowLogic => {
  const dispatch = useAppDispatch()
  const [params, setParams] = useSearchParams()

  const { userFeatures, userHasRight } = useUserRights()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [isSaveAndExit, setIsSaveAndExit] = useState(false)
  const timer = useRef<number>()
  const [polling, setPolling] = useState(false)

  const {
    user,
    accessToken,
    currentStep,
    emailServerSettings,
    isSaveDeploymentMethodInProgress,
    isSaveSetupFlowStepInProgress,
    isIpirScanStatusInProgress,
    isSetSelectedRegionInProgress,
    prepopulatedRegion,
    selectedRegion,
    emailServerVerified,
    defaultEmailServerConfig,
    emailServerConfig,
    emailAddress,
    verifyDomainState,
    mxRecords,
    oldMxRecords,
    newMxRecordsError,
    oldMxRecordsError,
    isSaveSettingsSuccess,
    isSaveSettingsInProgress,
    initializeSetupFlowInProgress,
    startInlineDeploymentInProgress,
    startInlineDeploymentSuccess,
    jobName,
    inlineDeploymentStatus,
    deploymentSetup,
    saveMsDomainInProgress,
    inlineDeploymentExecute,
    getDomainAvailabilityInProgress,
    availableDomains
  } = useAppSelector(_store => ({
    user: _store.auth.accessTokenObject,
    accessToken: _store.auth.accessToken,
    currentStep: _store.emailServer.setupFlowStep,
    emailServerSettings: _store.emailServer.emailServerSettings,
    isSaveDeploymentMethodInProgress: isPending(_store.emailServer.saveDeploymentMethodApiStatus),
    isSaveSetupFlowStepInProgress: isPending(_store.emailServer.saveSetupFlowStepApiStatus),
    isIpirScanStatusInProgress: isPending(_store.emailServer.getIpirScanStatusApiStatus),
    isSetSelectedRegionInProgress: isPending(_store.emailServer.setRegionApiStatus),
    emailServerVerified: _store.emailServer.emailServer?.verified,
    verifyDomainState: _store.emailServer.verifyDomainState,
    defaultEmailServerConfig: _store.emailServer.emailServer,
    emailServerConfig: _store.emailServer.updatedEmailServer,
    emailAddress: _store.emailServer.emailAddress,
    prepopulatedRegion: _store.emailServer.emailServerSettings?.region,
    selectedRegion: _store.emailServer.selectedRegion,
    sectionsIsVerifiedStates: _store.emailServer.emailFlowState.validatedSections,
    mxRecords: _store.emailServer.mxRecords,
    oldMxRecords: _store.emailServer.oldMxRecords,
    newMxRecordsError: _store.emailServer.emailFlowState.newRecordsError,
    oldMxRecordsError: _store.emailServer.emailFlowState.oldRecordsError,
    isSaveSettingsSuccess: isSuccess(_store.emailServer.setEmailServerSettingsApiStatus),
    isSaveSettingsInProgress: isPending(_store.emailServer.setEmailServerSettingsApiStatus),
    initializeSetupFlowInProgress: isPending(_store.emailServer.initializeSetupFlowApiStatus),
    startInlineDeploymentInProgress: isPending(_store.emailServer.startInlineDeploymentApiStatus),
    startInlineDeploymentSuccess: isSuccess(_store.emailServer.startInlineDeploymentApiStatus),
    getInlineDeploymentStatusSuccess: isSuccess(_store.emailServer.getInlineDeploymentStatusApiStatus),
    getInlineDeploymentStatusFailed: isFailed(_store.emailServer.getInlineDeploymentStatusApiStatus),
    jobName: _store.emailServer.inlineDeploymentJobName,
    inlineDeploymentStatus: _store.emailServer.inlineDeploymentStatus,
    deploymentSetup: _store.emailServer.deploymentSetup,
    saveMsDomainInProgress: isPending(_store.emailServer.saveMsDomainMethodApiStatus),
    inlineDeploymentExecute: _store.emailServer.inlineDeploymentExecute,
    getDomainAvailabilityInProgress: isPending(_store.emailServer.getDomainAvailabilityApiStatus),
    availableDomains: _store.emailServer.availableDomains
  }))

  useEffect(() => {
    trackPageLeaveMixpanelEvent(TRACKING_EVENTS.SETUP_FLOW.CLOSE)
    if (user && accessToken) {
      setIntercomUser(user, accessToken, { company: { finished_wizard: false } })
    }
    // eslint-disable-next-line
  }, [])

  const migrateToken = useMemo(() => {
    const migrateTokenSecret = emailServerSettings?.migrateToken
    if (!migrateTokenSecret) {
      return ''
    }

    return `?${appConfig.QUERY_PARAMS.MIGRATE_TOKEN}=${encodeURIComponent(migrateTokenSecret)}`
  }, [emailServerSettings])

  // BNEP-674: Always redirect to BarracudaOne on exit
  const wizardReturnUrl = useMemo(() => appConfig.BARRACUDA_ONE_URL ?? window.location.origin, [])

  // account settings are loaded
  useEffect(() => {
    // 1 - goto endUser app if user is endUser
    if (!appFeatures.DisabledWizardAdminCheck && userHasRight(UserRights.GOTO_ENDUSER_APP)) {
      gotoEnduserEntryPathFromWizard()
      // 2 - goto admin app if the wizard is completed
    } else if (emailServerSettings?.wizardCompleted) {
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.COMPLETE)
      gotoAdminLandingPage()
      // 3 - Forward the user to the selected region (if not matched with the current host)
    } else if (
      appFeatures.EnabledRegionChange &&
      emailServerSettings?.url &&
      emailServerSettings.url !== window.location.hostname
    ) {
      window.location.href = `${window.location.origin?.replace(
        window.location.host,
        emailServerSettings?.url || ''
      )}${routesConfig.BCC_LOGIN?.url()}${migrateToken}`
      // default - welcome the user in the wizard
    } else if (
      currentStep === SetupFlowCommonSteps.introduction &&
      (!emailServerSettings?.version || (userFeatures?.areWizardsDeprecated && emailServerSettings?.version !== 'v2'))
    ) {
      dispatch(initializeSetupFlow({ version: 'v2', ...props }))
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.START)
    }

    return () => {
      clearInterval(timer.current)
    }
  }, [props, emailServerSettings, currentStep, migrateToken, dispatch, userHasRight, userFeatures])

  // completed
  useEffect(() => {
    if (isSaveSettingsSuccess) {
      if (isSaveAndExit) {
        setIsRedirecting(true)
        window.location.href = wizardReturnUrl
      }
    }
  }, [dispatch, currentStep, isSaveSettingsSuccess, emailServerSettings, isSaveAndExit, wizardReturnUrl])

  // execute inline script on landing
  useEffect(() => {
    if (inlineDeploymentExecute) {
      dispatch(resetInlineDeployment())
      dispatch(startInlineDeployment())
    }
  }, [dispatch, inlineDeploymentExecute])

  // inline setup
  useEffect(() => {
    if (startInlineDeploymentSuccess && jobName) {
      clearInterval(timer.current)
      setPolling(true)
      timer.current = window.setInterval(
        () => dispatch(getInlineDeploymentStatus(jobName)),
        appConfig.INLINE_DEPLOYMENT.POLL_INTERVAL
      )
    }
  }, [dispatch, startInlineDeploymentSuccess, jobName])

  const steps = useMemo(() => {
    const keys = Object.keys(SetupFlowCommonSteps)
    if (props.ipirSection) {
      keys.splice(keys.length - 1, 0, ...Object.keys(SetupFlowIpirSteps))
    }
    if (props.egdSection) {
      keys.splice(keys.length - 1, 0, ...Object.keys(SetupFlowEgdSteps))
    }

    return keys.slice(0, keys.length - 1).map(step => {
      const result: StepInfo = { name: step as SetupFlowSteps }

      // Microsoft connected skipped steps
      if (
        (step === SetupFlowIpirSteps.microsoftConnected || step === SetupFlowEgdSteps.deploymentSetup) &&
        emailServerSettings?.microsoftConnected === false
      ) {
        result.skipped = true
      }

      // If the user is setting up domain different from the ms domains (not found) we only allow mx record setup
      const emailServerVerifiedName =
        emailServerSettings?.emailServers &&
        emailServerSettings.emailServers?.length > 0 &&
        emailServerSettings.emailServers[0].domainName
      if (
        step === SetupFlowEgdSteps.deploymentSetup &&
        emailServerVerifiedName &&
        !(emailServerSettings.microsoftVerifiedDomains || []).some(domain => domain.name === emailServerVerifiedName)
      ) {
        result.skipped = true
      }

      if (currentStep === step) {
        result.active = true
      }
      return result
    })
  }, [currentStep, emailServerSettings, props])

  // inline setup status
  useEffect(() => {
    if (
      inlineDeploymentStatus === INLINE_DEPLOYMENT_STATUSES.SUCCEEDED ||
      inlineDeploymentStatus === INLINE_DEPLOYMENT_STATUSES.FAILED
    ) {
      setPolling(false)
      clearInterval(timer.current)
      /* 
        When completed just update local state to setup completed page, this page is only visible after 
        success script run. Next time user comes in wizard is already marked as complete
       */
      if (inlineDeploymentStatus === INLINE_DEPLOYMENT_STATUSES.SUCCEEDED) {
        const current = steps.findIndex(step => step.name === currentStep)
        const nextStep = steps.find((step, index) => index > current && step.skipped !== true)?.name
        if (nextStep) {
          dispatch(setSetupFlowStep(steps[current + 1].name))
        }
      }
    }
  }, [currentStep, steps, dispatch, inlineDeploymentStatus])

  const onExit = useCallback(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.SAVE_AND_EXIT)
    dispatch(setEmailServerSettings({ completed: false }))
    setIsSaveAndExit(true)
  }, [dispatch])

  const onNext = useCallback(async () => {
    dispatch(resetSetupFlowError())
    params.delete('error')
    params.delete('idp_error')
    params.delete('error_description')
    params.delete('idp_error_description')
    params.delete('email')
    setParams(params, { replace: true })

    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.NEXT, { step: currentStep })
    const current = steps.findIndex(step => step.name === currentStep)
    let nextStep = steps.find((step, index) => index > current && step.skipped !== true)?.name
    switch (currentStep) {
      case SetupFlowCommonSteps.introduction:
        if (nextStep) {
          dispatch(saveSetupFlowStep({ nextStep }))
        }
        return
      case SetupFlowIpirSteps.microsoftConnected:
        if (nextStep) {
          dispatch(saveSetupFlowStep({ nextStep }))
        }
        return
      case SetupFlowEgdSteps.regionSelection:
        if (nextStep) {
          if (prepopulatedRegion) {
            dispatch(saveSetupFlowStep({ nextStep }))
          } else if (selectedRegion) {
            dispatch(setRegion({ region: selectedRegion.code.replace('GB', 'UK'), nextStep }))
          } else {
            dispatch(setSelectedRegionError(true))
          }
        }
        return
      case SetupFlowEgdSteps.verifyDomain: {
        if (emailServerSettings?.microsoftConnected === true && availableDomains.length > 0) {
          if (nextStep) {
            dispatch(msVerifyDomain(nextStep))
          }
        } else {
          const isValidEmail = isEmailValid(trim(emailAddress))
          setIsEmailInputError(false)
          setIsMissedServerDetection(false)
          setIsMissedServerVerify(false)

          switch (true) {
            case !isValidEmail:
              dispatch(setIsEmailInputError(true))
              break
            case isValidEmail && !defaultEmailServerConfig:
              dispatch(setIsMissedServerDetection(true))
              break
            case !defaultEmailServerConfig?.verified:
              dispatch(setIsMissedServerVerify(true))
              break
            default:
          }

          const isSectionHasError =
            Object.values(verifyDomainState).some(verifyDomainSerror => verifyDomainSerror) ||
            (!!emailServerConfig && !defaultEmailServerConfig?.server?.length)
          if (emailServerVerified && !isSectionHasError) {
            if (availableDomains.length === 0) {
              nextStep = steps.find((step, index) => index > current + 1 && step.skipped !== true)?.name
            }
            if (nextStep) {
              dispatch(
                setEmailServerSettings({
                  completed: false,
                  deploymentSetup: DeploymentSetupOptions.mxRecord,
                  nextStep
                })
              )
            }
          }
        }
        return
      }
      case SetupFlowEgdSteps.deploymentSetup:
        if (deploymentSetup === DeploymentSetupOptions.mxRecord) {
          if (nextStep) {
            dispatch(saveDeploymentMethod({ step: nextStep }))
          }
        } else if (deploymentSetup === DeploymentSetupOptions.inline) {
          routesConfig.SETUP_FLOW_IPIR_AUTHORIZE.goto({}, false, `?appType=${IPIRAppTypes.InlineMx}`)
        }
        return
      case SetupFlowEgdSteps.emailFlow: {
        if (deploymentSetup === DeploymentSetupOptions.inline) {
          dispatch(resetInlineDeployment())
          dispatch(startInlineDeployment())
        } else if (deploymentSetup === DeploymentSetupOptions.mxRecord) {
          const newMxVerified =
            !!mxRecords && !Object.values(mxRecords)?.some(record => !record.verified) && !newMxRecordsError
          const oldMxVerified =
            !!oldMxRecords && !Object.values(oldMxRecords)?.some(record => !record.verified) && !oldMxRecordsError

          if (!newMxVerified) {
            dispatch(setNewMxRecordsError('MX record needs to be verified to complete setup'))
          }

          if (!oldMxVerified) {
            dispatch(setOldMxRecordsError('Removal of old MX records needs to be verified to complete setup'))
          }

          if (newMxVerified && oldMxVerified) {
            if (user && accessToken) {
              setIntercomUser(user, accessToken, { company: { finished_wizard: true } })
            }
            dispatch(setEmailServerSettings({ completed: true }))
          }
        }
        break
      }
      case SetupFlowCommonSteps.setupCompleted: {
        gotoAdminEntryPathFromWizard('', true)
        break
      }
      default:
    }
  }, [
    dispatch,
    currentStep,
    emailServerVerified,
    selectedRegion,
    verifyDomainState,
    emailServerConfig,
    defaultEmailServerConfig,
    emailAddress,
    mxRecords,
    newMxRecordsError,
    oldMxRecords,
    oldMxRecordsError,
    accessToken,
    user,
    params,
    deploymentSetup,
    emailServerSettings?.microsoftConnected,
    setParams,
    steps,
    prepopulatedRegion,
    availableDomains
  ])

  return useMemo(
    () => [
      {
        currentStep,
        stepInProgress:
          isSaveSetupFlowStepInProgress ||
          isSaveDeploymentMethodInProgress ||
          isSetSelectedRegionInProgress ||
          isSaveSettingsInProgress ||
          saveMsDomainInProgress ||
          isRedirecting,
        loading: initializeSetupFlowInProgress || isIpirScanStatusInProgress || getDomainAvailabilityInProgress,
        inlineDeploymentInProgress: polling || startInlineDeploymentInProgress,
        completeSetupButton:
          (currentStep === SetupFlowEgdSteps.emailFlow && deploymentSetup === DeploymentSetupOptions.mxRecord) ||
          currentStep === SetupFlowCommonSteps.setupCompleted,
        nextDisabled:
          (currentStep === SetupFlowIpirSteps.microsoftConnected &&
            !emailServerSettings?.ipirAccountId &&
            !emailServerSettings?.ipirTokenId) ||
          (currentStep === SetupFlowEgdSteps.verifyDomain &&
            !!emailServerSettings?.microsoftVerifiedDomains?.length &&
            availableDomains.length === 0),
        steps
      },
      {
        onExit,
        onNext
      }
    ],
    [
      currentStep,
      isSaveSetupFlowStepInProgress,
      isSaveDeploymentMethodInProgress,
      isIpirScanStatusInProgress,
      getDomainAvailabilityInProgress,
      isSetSelectedRegionInProgress,
      initializeSetupFlowInProgress,
      isSaveSettingsInProgress,
      saveMsDomainInProgress,
      isRedirecting,
      emailServerSettings,
      polling,
      startInlineDeploymentInProgress,
      deploymentSetup,
      steps,
      availableDomains,
      onExit,
      onNext
    ]
  )
}
